import { AppError } from "../app-error/app-error.model";
import { AuthenticationType } from "../auth-transaction/auth-transaction.model";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";
import { LoginMethod } from "../telemetry/auth/auth-telemetry.model";

import { AuthState } from "./auth.model";

export const enum LoginType {
	UsernamePassword = "up",
	VerificationCode = "tfa",
	PersonalNumber = "uppn",
	Token = "token",
	OneTimeToken = "onetimetoken",
	SuspiciousLogin = "suspiciouslogin",
}

export const enum SessionErrorStatus {
	Expired = "Expired",
	ExpiryCheckInProgress = "Expired Check In Progress",
	Invalid = "Invalid",
	SessionLimitExpired = "Session Limit Expired",
}

export const enum SessionCustomEvent {
	obgLoginSuccess = "obgLoginSuccess",
	obgLoginFail = "obgLoginFail",
	obgLogout = "obgLogout",
}

/**
 * WARNING!! TRY TO AVOID USING THIS STATE OUTSIDE OF `ngx.core/auth` FOLDER MUCH AS POSSIBLE!!
 * ACCESS TO `SessionState` WILL NOT BE ANY MORE ALLOWED SOON.
 * If you need to use this state outside of `ngx.core/auth` folder, please first contact core team.
 * What is this: This is state definition required to mantain current session token.
 * All the application logic will be refactored soon not to use SessionState directly
 */
export interface SessionState extends AuthState {
	errorCode?: string;
	sessionErrorStatus?: SessionErrorStatus;
	sessionErrorStatusMessage?: string;
	termsAndConditionsToken?: string;
	termsAndConditionsResourceKeys?: string[];
	privacyPolicyToken?: string;
	latestEntityLegalNoticeToken?: string;
	latestEntityLegalNoticeBodyKey?: string;
	consentsNotAcceptedToken?: string;
	customerNotVerifiedData?: CustomerNotVerifiedData;
	profileNotCompletedData?: ProfileNotCompletedData;
	lastActiveUsername?: string;
	isLastActiveUser?: boolean;
	nextTimeLogin?: string;
	customerCanLogInAt?: string;
	shouldRememberUser?: boolean;
	oneTimeLoginToken?: string;
	remainingAttempts?: number;
	authThroughLogin?: boolean;
	redirectBrand?: boolean;
	customerId?: string;
	loginToken?: string;
	countryCode?: string;
}

export interface ProfileNotCompletedData {
	profileConfirmationToken: string;
	countryCode: string;
}
export interface CustomerNotVerifiedData {
	token?: string;
	personalNumber?: string;
	transactionId?: string;
	autostartToken?: string;
	authenticationType?: AuthenticationType;
}

export interface CancelVerificationData {
	transactionTimeout?: boolean;
	customerNotVerifiedData: CustomerNotVerifiedData;
}

// todo: refactor in API
export interface SessionTokenDecoded {
	UserId: string;
	LoginSessionId: string;
	Jurisdiction: number;
}

export interface LoginRequest {
	username?: string;
	password?: string;
	token?: string;
	iovationBlackBox?: string;
	fraudToken?: string;
	shouldRememberUser?: boolean;
	deviceId?: string;
	platform?: string;
	options?: LoginRequestOption;
}

export interface LoginRequestOption {
	disallowRedirect?: boolean;
	disallowLogin?: boolean;
}

export interface LoginWithPersonalNumberRequest extends LoginRequest {
	personalNumber: string;
}

export interface LoginWithTokenRequest {
	token: string;
	iovationBlackBox?: string;
	authenticationMethod?: LoginMethod;
	isOneTimeLoginPassword?: boolean;
	fraudToken?: string;
}

export interface LoginWithVerificationCodeRequest {
	username: string;
	verificationCode: string;
	iovationBlackBox?: string;
	shouldRememberUser?: boolean;
	deviceId?: string;
	platform?: string;
}

// TODO: AOCC-11506 it has been move over from `libs/common/packages/ngx.common/src/two-factor-auth`
// to avoid circular dependencies. It might be worth considering moving part of that state
// into `libs/core/ngx.core`
export enum TwoFactorAuthMethod {
	EmailTwoFactorAuthenticationMethod = "EmailTwoFactorAuthenticationMethod",
	SmsTwoFactorAuthenticationMethod = "SmsTwoFactorAuthenticationMethod",
}

export interface TwoFactorAuthLogin extends LoginWithVerificationCodeRequest {
	type: LoginType;
	method: TwoFactorAuthMethod;
	rememberDevice?: boolean;
}

export interface TwoFactorAuthLoginSuccessPayload extends LoginSuccessfulPayload {
	method: TwoFactorAuthMethod;
}

export interface TwoFactorAuthLoginFailPayload {
	method: TwoFactorAuthMethod;
	error: AppError;
}

export interface SessionResponse extends TimeToLiveResponse {
	sessionToken: string;
}

export interface LoginSuccessfulResponse extends SessionResponse {
	hashedEmail?: string;
	customerId: string;
	jurisdiction: Jurisdiction;
	isFirstLogin: boolean;
	personalSessionLimitCheckPeriodSeconds: number;
	authenticationMethod?: LoginMethod;
}

export interface LoginSuccessfulPayload extends LoginSuccessfulResponse {
	shouldRememberUser?: boolean;
	username?: string;
}

export interface TimeToLiveResponse {
	timeToLiveSeconds: number;
}

export interface LogoutPayload {
	shouldSkipNotification?: boolean;
	isUserFraud?: boolean;
}

export interface SessionLimitNextTimeLoginResponse {
	customerCanLogInAt: string;
}

export interface SessionError extends AppError {
	customerCanLogInAt?: string;
	remainingAttempts?: number;
}

export interface SessionLimitExpiredMessage {
	code: string;
	customerCanLogInAt?: string;
}

export interface SessionDomEventPayload {
	date: Date;
}

export interface CustomerDataNotConfirmedResponse {
	code: string;
	customerDataConfirmationToken: string;
	jurisdiction?: string;
	oneTimeLoginToken?: string;
	authenticationToken: string;
	customerCountry: string;
}

export interface LatestTCandPPNotAcceptedLoginResponseData {
	jurisdiction?: Jurisdiction;
	oneTimeLoginToken: string;
	privacyPolicyResourceKeys?: string[];
	termsAndConditionsResourceKeys?: string[];
	privacyPolicyToken?: string;
	termsAndConditionsToken?: string;
}

export interface LatestTCandPPNotAcceptedResponse {
	code: string;
	latestTcNotAcceptedLoginResponse?: LatestTCandPPNotAcceptedLoginResponseData;
	latestPrivacyPolicyNotAcceptedLoginResponse?: LatestTCandPPNotAcceptedLoginResponseData;
}

export interface CustomerCountryRedirectionConfig {
	areCountriesForbidden?: boolean;
	customerCountries?: string[];
	redirectionDomain?: string;
}
